/* eslint-disable react/jsx-props-no-spreading */
import React, { FC, useCallback, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Icons, Positions, Sizes } from '@sede-enterprise/shell-ds-react-framework';
import { FormProvider, useForm } from 'react-hook-form';
import { Modal } from '../../NominationGroups/style';
import { Col, FormRow } from 'src/UI/Grid';
import Button from 'src/UI/Button';
import ErrorMessage from 'src/UI/ErrorMessage';
import FormControlTextInput from 'src/UI/FormComponent/FormControlTextInput';
import FormControlSelect from 'src/UI/FormComponent/FormControlSelect';
import FormControlDatePicker from 'src/UI/FormComponent/FormControlDatePicker';
import { post } from 'src/services/GenesisApi';
import { sendNotification } from 'src/UI/Notification';
import { MAX_LENGTH_20, MAX_LENGTH_50, MESSAGES, SET_MARKET } from 'src/constants/EUMarkets';
import { IAddEditMarket, IMarketBalance, ISetMarketBalance } from 'src/types/EUMarkets.type';
import { handleError } from 'src/utils/common/utils';
import { formatDateOnly, isDateRangeValid } from '../../Locations/AddEditParameter';
import InlineBalanceList from './InlineBalanceList';

const { Add: AddIcon } = Icons;

const toBalanceDto = (balance: IMarketBalance): ISetMarketBalance => ({
  id: balance.id,
  balanceName: balance.balanceName,
  organizationId: balance.organizationId,
  validFrom: balance.validFrom,
  // Valid To is optional; send null for open ended balances.
  validTo: balance.validTo || null,
});

// Drafts are keyed by row index, so removing a row has to shift the later keys down.
const removeAndReindexRecord = <T,>(records: Record<number, T>, removedIndex: number) =>
  Object.entries(records).reduce<Record<number, T>>((next, [key, value]) => {
    const index = Number(key);
    if (index === removedIndex) {
      return next;
    }
    next[index > removedIndex ? index - 1 : index] = value;
    return next;
  }, {});

const defaultValues = {
  marketName: '',
  shortName: '',
  marketType: '',
  validFrom: dayjs(),
  validTo: null,
};

const marketTypeOptions = [
  { id: 'Country', name: 'Country' },
  { id: 'Pipeline', name: 'Pipeline' },
];

const AddEditMarket: FC<IAddEditMarket> = ({
  open = false,
  onClose = () => {},
  market = null,
  refreshData = () => {},
}) => {
  const methods = useForm();
  const { reset, setValue, watch, setError, clearErrors } = methods;
  const [loading, setLoading] = useState<boolean>(false);
  const [balances, setBalances] = useState<IMarketBalance[]>([]);
  const [balanceDrafts, setBalanceDrafts] = useState<Record<number, IMarketBalance>>({});
  const [balanceDraftErrors, setBalanceDraftErrors] = useState<Record<number, string>>({});
  const [pendingBalanceCount, setPendingBalanceCount] = useState(0);
  const validFrom = watch?.('validFrom');
  const validTo = watch?.('validTo');
  const balancesErrorMessage = methods.formState?.errors?.balances?.message as string | undefined;

  const resetBalanceState = () => {
    setBalances([]);
    setBalanceDrafts({});
    setBalanceDraftErrors({});
    setPendingBalanceCount(0);
  };

  useEffect(() => {
    if (market) {
      reset({
        marketName: market.marketName,
        shortName: market.shortName,
        marketType: market.marketType,
        validFrom: market.validFrom ? dayjs(market.validFrom) : undefined,
        validTo: market.validTo ? dayjs(market.validTo) : null,
      });
      setBalances(market.balances ?? []);
      setBalanceDrafts({});
      setBalanceDraftErrors({});
      setPendingBalanceCount(0);
      return;
    }
    reset(defaultValues);
    resetBalanceState();
  }, [market, open]);

  const handleClose = () => {
    reset(defaultValues);
    resetBalanceState();
    clearErrors('balances');
    onClose();
  };

  const handleBalanceDraftChange = useCallback(
    (balance: IMarketBalance, rowIndex: number, error?: string) => {
      setBalanceDrafts((currentDrafts) => ({ ...currentDrafts, [rowIndex]: balance }));
      setBalanceDraftErrors((currentErrors) => ({ ...currentErrors, [rowIndex]: error ?? '' }));
      // Clear the summary message as soon as the user edits, otherwise a stale error from the
      // previous save attempt stays on screen even once the row is valid.
      if (!error) {
        clearErrors('balances');
      }
    },
    [clearErrors],
  );

  const handleCancelBalanceAdd = useCallback((balanceIndex?: number) => {
    setPendingBalanceCount((count) => Math.max(count - 1, 0));
    if (balanceIndex === undefined) {
      return;
    }
    setBalanceDrafts((currentDrafts) => removeAndReindexRecord(currentDrafts, balanceIndex));
    setBalanceDraftErrors((currentErrors) => removeAndReindexRecord(currentErrors, balanceIndex));
  }, []);

  const handleValidFromChange = (newValidFrom: Dayjs | null) => {
    if (newValidFrom && validTo && dayjs(newValidFrom).isAfter(dayjs(validTo), 'day')) {
      setValue('validTo', null, { shouldValidate: true });
    }
  };

  const handleOnSubmit = (formData) => {
    // Balances are sent as a full replacement set, so every surviving row must be included.
    const mergedBalances = [
      ...balances.map((balance, index) => balanceDrafts[index] ?? balance),
      ...Array.from(
        { length: pendingBalanceCount },
        (_, pendingIndex) => balanceDrafts[balances.length + pendingIndex] ?? null,
      ).filter((balance): balance is IMarketBalance => Boolean(balance)),
    ];

    const firstDraftError = Object.values(balanceDraftErrors).find(Boolean);
    if (firstDraftError) {
      setError('balances', { type: 'validate', message: firstDraftError });
      return;
    }

    clearErrors('balances');
    setLoading(true);
    const failureMessage = market ? MESSAGES.UPDATED_MARKET_FAILED : MESSAGES.CREATED_MARKET_FAILED;
    post(SET_MARKET, {
      id: market?.id ?? 0,
      marketName: formData.marketName,
      shortName: formData.shortName,
      marketType: formData.marketType,
      isActive: market?.isActive ?? true,
      validFrom: formatDateOnly(formData.validFrom as Dayjs),
      validTo: formData.validTo ? formatDateOnly(formData.validTo as Dayjs) : null,
      balances: mergedBalances.map(toBalanceDto),
    })
      .then(() => {
        sendNotification({
          type: 'success',
          message: market ? MESSAGES.UPDATED_MARKET : MESSAGES.CREATED_MARKET,
        });
        refreshData();
        handleClose();
      })
      .catch((error) => {
        handleError(error, failureMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const renderFooter = () => (
    <div className='button-style'>
      <Button type='button' onClick={handleClose} variant='outlined' data-testid='btn-cancel'>
        Cancel
      </Button>
      <Button
        data-testid='submit-form'
        type='button'
        loading={loading}
        disabled={loading}
        onClick={methods.handleSubmit(handleOnSubmit)}
      >
        Save
      </Button>
    </div>
  );

  return (
    <Modal
      className='market-edit-modal'
      title={`${market ? 'Modify' : 'Add a new'} market`}
      open={open}
      onClose={handleClose}
      footer={renderFooter()}
      maskClosable={false}
      isDraggable
      width={960}
    >
      <FormProvider {...methods}>
        <FormRow>
          <Col size={6}>
            <FormControlTextInput
              controllerProps={{
                name: 'marketName',
                rules: {
                  required: MESSAGES.REQUIRED_MARKET_NAME,
                  maxLength: {
                    value: MAX_LENGTH_50,
                    message: MESSAGES.MAX_LENGTH_50,
                  },
                },
              }}
              label='Market Name'
              methods={methods}
              inputProps={{ placeholder: 'Enter Market Name' }}
            />
          </Col>
          <Col size={6}>
            <FormControlTextInput
              controllerProps={{
                name: 'shortName',
                rules: {
                  required: MESSAGES.REQUIRED_SHORT_NAME,
                  maxLength: {
                    value: MAX_LENGTH_20,
                    message: MESSAGES.MAX_LENGTH_20,
                  },
                },
              }}
              label='Short Name'
              methods={methods}
              inputProps={{ placeholder: 'Enter Short Name' }}
            />
          </Col>
        </FormRow>
        <FormRow>
          <Col size={4}>
            <FormControlSelect
              controllerProps={{
                name: 'marketType',
                rules: {
                  required: MESSAGES.REQUIRED_MARKET_TYPE,
                },
              }}
              label='Market Type'
              methods={methods}
              data={marketTypeOptions}
              isLoading={false}
            />
          </Col>
          <Col size={4}>
            <FormControlDatePicker
              methods={methods}
              label='Valid From'
              controllerProps={{
                name: 'validFrom',
                rules: {
                  required: MESSAGES.REQUIRED_VALID_FROM,
                  validate: (value) =>
                    isDateRangeValid(value, validTo) || MESSAGES.INVALID_PARAMETER_DATE_RANGE,
                },
              }}
              handleOnChange={handleValidFromChange}
            />
          </Col>
          <Col size={4}>
            <FormControlDatePicker
              methods={methods}
              label='Valid To'
              controllerProps={{
                name: 'validTo',
                rules: {
                  validate: (value) =>
                    isDateRangeValid(validFrom, value) || MESSAGES.INVALID_PARAMETER_DATE_RANGE,
                },
              }}
              inputProps={{
                disabledDate: (current: Dayjs) =>
                  validFrom ? current.isBefore(validFrom, 'day') : false,
              }}
            />
          </Col>
        </FormRow>
        <div className='group-details group-details-with-action'>
          <strong className='param-title'>Balances</strong>
          <Button
            onClick={() => setPendingBalanceCount((count) => count + 1)}
            icon={<AddIcon />}
            size={Sizes.Small}
            variant='filled'
            iconPosition={Positions.Right}
            data-testid='add-market-balance'
          >
            Add
          </Button>
        </div>
        <div className='parameter-table balance-list-scroll'>
          <InlineBalanceList
            open={open}
            marketId={market?.id ?? 0}
            balances={balances}
            addingBalanceCount={pendingBalanceCount}
            marketValidFrom={validFrom ? dayjs(validFrom) : null}
            marketValidTo={validTo ? dayjs(validTo) : null}
            onDraftChange={handleBalanceDraftChange}
            onCancelAdd={handleCancelBalanceAdd}
          />
          {balancesErrorMessage && <ErrorMessage>{balancesErrorMessage}</ErrorMessage>}
        </div>
      </FormProvider>
    </Modal>
  );
};

export default AddEditMarket;
